import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { LatestPostResultsDto, MonitoringLatestPostResultsDto } from '../../../../../../common/general-types/super-social.dto';
import { FormatService } from '../../../../../../common/format/format.service';
import { ImageService } from '../../../../../../services/image/image.service';
import { DatePipe } from '@angular/common';
import { ToastNotificationService } from '../../../../../../atoms/toast-notification/toast-notification.service';
import { LoadingInlineComponent } from '../../../../../../atoms/loading-inline/loading-inline.component';
import { CreatorService } from '@/app/services/creator/creator.service';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { SubmittedPostDto } from '@/app/common/general-types/submitted-posts.dto';
import { take } from 'rxjs';
import { CreatorProfileComponent } from '@/app/pages/creators/molecules/creator-profile/creator-profile.component';
import { TooltipDirective } from '@/app/common/directives/tooltip/tooltip.directive';
import { SocialChannelEnum } from '@/app/common/general-types/briefs.dto';

@Component({
  selector: 'mbtr-content-performance',
  standalone: true,
  imports: [DatePipe, LoadingInlineComponent, CreatorProfileComponent, TooltipDirective],
  templateUrl: './content-performance.component.html',
  styleUrl: './content-performance.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ContentPerformanceComponent implements OnInit, OnChanges {
  @Input() postsList: any;
  @Output('jumpToContent') jumpToContent = new EventEmitter<LatestPostResultsDto | MonitoringLatestPostResultsDto>();
  @Output('downloadResultsCsv') downloadResultsCsv = new EventEmitter();
  @Input() schedulePillLabel: string | null = null;
  @Output() schedulePill = new EventEmitter<void>();
  @Output() refreshResults = new EventEmitter<void>();

  superSocialResults = signal<LatestPostResultsDto[] | MonitoringLatestPostResultsDto[] | undefined>(undefined);

  sortSelected = signal<'handle' | 'createTime' | 'followers' | 'views'>('createTime');

  loadingCreatorPortfolio = signal<string | undefined>(undefined);
  creatorPortfolio = signal<CreatorResponseDto | undefined>(undefined);

  /** Cached refresh age per result — avoids NG0100 from clock-based values changing between CD passes. */
  private refreshAgeByResult = computed(() => new Map(
    (this.superSocialResults() ?? []).map((result: LatestPostResultsDto | MonitoringLatestPostResultsDto) => [result, {
      label: this.formatService.getTimeSinceUpdated(result.updatedDate),
      ageClass: this.getRefreshAgeClass(result.updatedDate)
    }])
  ));

  /** Services */
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  private toastNotificationService = inject(ToastNotificationService);
  private creatorService = inject(CreatorService);
  /** Services - END */

  ngOnInit(): void {
    this.syncPostsList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postsList']) {
      this.syncPostsList();
    }
  }

  private syncPostsList(): void {
    if (!this.postsList) {
      this.superSocialResults.set(undefined);
      return;
    }
    this.superSocialResults.set(Object.keys(this.postsList).map((postId) => this.postsList[postId]));
  }

  refreshAge(result: LatestPostResultsDto | MonitoringLatestPostResultsDto) {
    return this.refreshAgeByResult().get(result)!;
  }

  sortBy(sortOption: 'handle' | 'createTime' | 'followers' | 'views') {
    this.sortSelected.set(sortOption);
    if (sortOption === 'handle') {
      this.superSocialResults()?.sort((a, b) => a.handle.localeCompare(b.handle));
    } else if (sortOption === 'createTime') {
      this.superSocialResults()?.sort((a, b) => new Date(b.createTime).getTime() - new Date(a.createTime).getTime());
    } else if (sortOption === 'followers') {
      this.superSocialResults()?.sort((a, b) => b.followers - a.followers);
    } else if (sortOption === 'views') {
      this.superSocialResults()?.sort((a, b) => b.views - a.views);
    }
  }

  getRefreshAgeClass(dataRefreshDate: Date): string {
    if (dataRefreshDate) {
      const hoursAgo = Math.floor((new Date().getTime() - new Date(dataRefreshDate).getTime()) / (1000 * 60 * 60));
      if (hoursAgo < 8) {
        return 'text-green-500';
      } else if (hoursAgo < 24) {
        return 'text-yellow-500';
      } else {
        return 'text-red-500';
      }
    }
    return 'text-gray-500';
  }

  copyAdCode(adCode: string) {
    if (adCode) {
      navigator.clipboard.writeText(adCode);
    }
    this.toastNotificationService.alert({
      title: 'Copied!',
      message: 'Ad code copied to clipboard!',
      type: 'success',
      icon: 'success'
    }); 
  }

  downloadContent(){
    console.log('downloadContent');
    this.downloadResultsCsv.emit(true);
  }

  openVideoFeedback(post: LatestPostResultsDto) {
    console.log('launchPortfolio', post);
    this.jumpToContent.emit(post);
  }

  openPostInNewTab(post: SubmittedPostDto) {
    console.log('openPostInNewTab', post.videoLink);
    window.open(post.videoLink, '_blank');
  }

  openCreatorProfile(result: LatestPostResultsDto | MonitoringLatestPostResultsDto) {
    const creator = 'submittedPost' in result
      ? result.submittedPost?.creator
      : (result as MonitoringLatestPostResultsDto).creator;
    if (creator) {
      console.log('openCreatorProfile', creator.blazeFirebaseUid);
      this.loadingCreatorPortfolio.set(creator.blazeFirebaseUid);
      this.creatorService.getCreatorById(creator.blazeFirebaseUid).pipe(take(1)).subscribe((creator: CreatorResponseDto) => {
        console.log('creator', creator);
        this.creatorPortfolio.set(creator);
        this.loadingCreatorPortfolio.set(undefined);
      });
    }
  }

  isInstagramStoryResult(result: LatestPostResultsDto | MonitoringLatestPostResultsDto): boolean {
    return Number(result.submittedPost?.socialChannel) === SocialChannelEnum['Instagram Story'];
  }

  getPhotoURL(result: LatestPostResultsDto | MonitoringLatestPostResultsDto): string {
    const submittedPost = 'submittedPost' in result ? result.submittedPost : undefined;
    if (submittedPost?.creator?.photoURL) {
      return submittedPost.creator.photoURL;
    }
    if ('creator' in result && (result as MonitoringLatestPostResultsDto).creator?.photoURL) {
      return (result as MonitoringLatestPostResultsDto).creator.photoURL;
    }
    return result.profilePicture || '';
  }

  getCreatorBlazeFirebaseUid(result: LatestPostResultsDto | MonitoringLatestPostResultsDto): string {
    const submittedPost = 'submittedPost' in result ? result.submittedPost : undefined;
    if (submittedPost?.creator?.blazeFirebaseUid) {
      return submittedPost.creator.blazeFirebaseUid;
    }
    if ('creator' in result && (result as MonitoringLatestPostResultsDto).creator?.blazeFirebaseUid) {
      return (result as MonitoringLatestPostResultsDto).creator.blazeFirebaseUid;
    }
    return '';
  }
}
